import { useCallback, useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { Button } from "./ui/button"
import { ArrowRight } from "lucide-react"
import { cn } from "../lib/utils"
import { useAuth } from "../contexts/AuthContext"
import { usePlan } from "../hooks/usePlan"
import { planesDeContratacion } from "../data/planes"
import TarjetaDePlan from "./business/TarjetaDePlan"
import { API_URL } from '../config/api';


function PricingSection({ className, showCurrentPlan = false, isOnboarding = false, onPlanSelect }) {
  const [currentPlan, setCurrentPlan] = useState(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const { currentUser } = useAuth()
  const { refreshPlan } = usePlan()

  /**
   * Los planes salen de data/planes.js, la misma lista que muestra la landing.
   *
   * Acá había una copia escrita a mano con OTROS nombres —"Negocio", "Negocio
   * Pro", "Negocio Pro+"— y con los dos últimos marcados como "Próximamente"
   * aunque el backend los cobra sin problema: se anunciaba un plan con un
   * nombre en la landing y se cobraba con otro acá, y uno que la landing vendía
   * no se podía contratar. Con una sola lista eso no puede volver a pasar.
   *
   * Se traduce a la forma que espera el render de abajo (`features` como
   * objetos con nombre y si está incluido). Las de planes.js son frases sueltas
   * y todas son cosas que el plan incluye, así que van todas en `included`.
   */
  const tiers = planesDeContratacion.map((plan) => ({
    // El plan entero va adjunto: es lo que dibuja TarjetaDePlan, la misma
    // tarjeta que usa la landing.
    plan,
    id: plan.id,
    name: plan.nombre,
    description: plan.bajada,
    price: plan.precio,
    priceText: plan.precioTexto,
    priceInCentavos: plan.precioEnCentavos,
    features: plan.features.map((texto) => ({ name: texto, included: true })),
    highlight: plan.destacado,
    badge: plan.etiqueta,
    trialDays: plan.diasDePrueba,
    comingSoon: false,
  }));

  /* El filtro por "personal" / "business" se eliminó junto con los planes
     personales: dejaba la lista vacía cuando el interruptor estaba en
     "Personal", que además era su valor inicial. */
  const filteredTiers = tiers;

  // La función va con useCallback y el efecto la declara como dependencia.
  // Definida antes del efecto a propósito: con `const` no hay hoisting, así que
  // nombrarla en la lista de dependencias exige que ya exista.
  const fetchCurrentPlan = useCallback(async () => {
    try {
      const token = await currentUser.getIdToken()
      const response = await fetch(`${API_URL}/api/plan/current`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      
      if (response.ok) {
        const data = await response.json()
        setCurrentPlan(data.plan)
      }
    } catch (error) {
      console.error('Error fetching current plan:', error)
    }
  }, [currentUser])

  useEffect(() => {
    if (showCurrentPlan && currentUser) {
      fetchCurrentPlan()
    }
  }, [currentUser, showCurrentPlan, fetchCurrentPlan])

  // Función para pago de prueba (desarrollo) - actualiza el plan real
  const handleTestPayment = async (tier) => {
    if (!currentUser) {
      window.location.href = '/auth/login'
      return
    }

    try {
      setLoading(true)
      setMessage('Simulando pago de prueba...')
      
      const token = await currentUser.getIdToken()
      
      console.log('🔍 DEBUG: Enviando planId al backend:', tier.id)
      console.log('🔍 DEBUG: Datos completos del tier:', tier)
      
      // Los planes ahora se manejan directamente en el backend sin mapeo
      
      // Actualizar el plan usando el endpoint real
      const response = await fetch(`${API_URL}/api/plan/change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          planId: tier.id, // Usar el ID real directamente
          originalPlanId: tier.id, // Guardar el ID original para display
          planDisplayName: tier.name, // Guardar el nombre para mostrar
          billingCycle: 'monthly',
          paymentStatus: 'approved',
          paymentId: `test_payment_${Date.now()}`,
          amount: tier.priceInCentavos,
          isTestPayment: true
        }),
      })

      if (response.ok) {
        setMessage('¡Pago de prueba exitoso! Actualizando plan...')
        
        // Refrescar el plan en el contexto
        await refreshPlan()
        
        setMessage('¡Plan actualizado! Redirigiendo al dashboard...')
        
        // Guardar información del plan temporalmente en localStorage para display correcto
        localStorage.setItem('temp_plan_display', JSON.stringify({
          originalId: tier.id,
          displayName: tier.name,
          timestamp: Date.now()
        }));
        
        setTimeout(() => {
          window.location.href = '/admin/dashboard'
        }, 1500)
      } else {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Error al actualizar el plan')
      }
      
    } catch (error) {
      console.error('Error en pago de prueba:', error)
      setMessage(`Error: ${error.message}`)
      setLoading(false)
    }
  }

  const handlePlanSelection = async (tier) => {
    if (!currentUser) {
      window.location.href = '/auth/login'
      return
    }

    try {
      setLoading(true)
      setMessage('Procesando pago...')
      
      const token = await currentUser.getIdToken()
      
      // URLs de retorno - Usar URL pública para MercadoPago
      const returnUrl = `https://example.com/admin/plans`
      const cancelUrl = `https://example.com/admin/plans`
      
      // Crear suscripción recurrente en MercadoPago
      const paymentResponse = await fetch(`${API_URL}/api/subscriptions/create-preapproval`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          // Sólo el plan. El precio, el nombre y la URL del webhook los pone el
          // backend desde src/planes.js: mandarlos desde el navegador era lo
          // que permitía contratar un plan a un precio que no es el suyo
          // (ítem [24a]).
          planId: tier.id,
          billingCycle: 'monthly',
          userEmail: currentUser.email,
          returnUrl,
          cancelUrl
        }),
      })
      
      if (paymentResponse.ok) {
        const paymentData = await paymentResponse.json()
        
        if (paymentData.initPoint) {
          setMessage('Redirigiendo a MercadoPago...')
          // Redirigir a MercadoPago
          window.location.href = paymentData.initPoint
        } else {
          throw new Error('No se recibió URL de pago de MercadoPago')
        }
      } else {
        const error = await paymentResponse.json()
        setMessage(`Error al crear el pago: ${error.message}`)
      }
    } catch (error) {
      console.error('Error processing plan selection:', error)
      setMessage('Error al procesar la selección del plan')
    } finally {
      setLoading(false)
    }
  }

  const isCurrentPlan = (tierId) => {
    return currentPlan?.id === tierId || currentPlan?.planId === tierId
  }

  return (
    /* Dentro del onboarding la sección va sin aire de página completa ni
       título: el paso ya se llama "Elegí tu plan" y arriba está el botón de la
       prueba gratis, así que un segundo título ("Precios simples y
       transparentes") y 128px de relleno dejaban las tarjetas media pantalla
       más abajo, con un hueco vacío en el medio. */
    <section className={cn(
      "relative text-foreground overflow-hidden",
      // Fondo propio sólo en el onboarding, que la monta sobre el fondo del
      // paso. En /admin/plans la sección va adentro de una caja blanca que se
      // apoya sobre el fondo animado: con `bg-background` acá, la sección
      // pintaba de punta a punta y le tapaba el fondo a la página entera.
      isOnboarding && "bg-background",
      // pt-3: la etiqueta ("Más elegido") sale por encima del borde de la
      // tarjeta, y sin ese aire la sección la recorta.
      isOnboarding ? "pt-3 pb-0" : "py-12 md:py-24 lg:py-32",
      className
    )}>
      {/* El mismo ancho que la sección de planes de la landing (max-w-6xl):
          con la tarjeta compartida, las proporciones solo coinciden si el
          contenedor también coincide. A 896px las tarjetas quedaban en 283 y la
          etiqueta se comía el título. */}
      {/* Sin relleno horizontal propio: el margen lateral lo pone quien la
          monta —el paso del onboarding y la caja blanca de /admin/plans ya
          tienen el suyo—. Tres rellenos anidados dejaban las tarjetas en 256px
          a 1024px de ancho, y ahí "A medida" no entra en una línea. */}
      <div className="w-full mx-auto max-w-6xl">
        <div className={cn("flex flex-col items-center gap-4", isOnboarding ? "mb-0" : "mb-12")}>
          {!isOnboarding && (
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 dark:text-zinc-50 text-center px-4 sm:px-6 md:px-8 max-w-4xl mx-auto">
              Precios simples y transparentes
            </h2>
          )}
          
          {message && (
            <div className={`p-4 rounded-lg mb-4 ${message.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
              {message}
            </div>
          )}

          {showCurrentPlan && currentPlan && (
            <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full mb-4">
              <span>Plan actual: <strong>{currentPlan.name || currentPlan.planId}</strong></span>
            </div>
          )}
          
          {/* Acá había un interruptor "Personal / Negocio". Se eliminó junto con
              los planes personales: no filtraba nada —la lista es una sola— y
              ofrecía una categoría de planes que ya no existe. */}
        </div>

        {/* Tantas columnas como planes haya, hasta tres: con dos planes en una
            grilla de tres quedaba una columna vacía a la derecha y las tarjetas
            corridas hacia un costado. */}
        <div className={cn(
          "grid grid-cols-1 gap-6",
          filteredTiers.length >= 3 ? "lg:grid-cols-3" : "md:grid-cols-2"
        )}>
          {filteredTiers.map((tier) => (
            /* La tarjeta es la misma que la de la landing
               (components/business/TarjetaDePlan.jsx). Acá estaba escrita
               aparte, con otras proporciones, otro tilde y botones negros: al
               lado de la sección de planes de la home parecían dos productos
               distintos. Lo único propio de este selector son los botones, que
               cobran en vez de llevar a registrarse. */
            <TarjetaDePlan
              key={tier.id}
              plan={tier.plan}
              resaltado={isCurrentPlan(tier.id) || undefined}
            >
                {tier.plan.cotizado ? (
                  /* El plan cotizado no se contrata solo: no tiene precio y en
                     el backend es `cobrable: false`, así que un botón de pago
                     acá terminaría en un 400. Se saca un turno con nosotros en
                     nuestra propia página pública (`plan.enlace`, una ruta
                     interna), que de paso es la demo del producto. */
                  <Button asChild size="lg" variant="outline" className="w-full">
                    <Link to={tier.plan.enlace}>
                      {tier.plan.cta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                ) : tier.comingSoon ? (
                  /* Planes próximamente - mostrar como deshabilitados */
                  <Button
                    disabled={true}
                    size="lg"
                    className="w-full bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-60"
                  >
                    <div className="flex items-center justify-center">
                      <span className="text-lg">⏳ Próximamente</span>
                    </div>
                  </Button>
                ) : isOnboarding ? (
                  <>
                    {/* Para onboarding: mostrar opciones de 14 días gratis o pagar ahora */}
                    {/* Solo planes base (personal o business-starter) tienen opción de trial */}
                    {(tier.id === 'personal' || tier.id === 'business-starter') ? (
                      <>
                        {/* El botón de la app, como en la landing. Era un
                            degradado verde con emoji, sombra y un salto al pasar
                            el mouse: el único de ese color en toda la pantalla,
                            y no se parecía a ningún otro botón del producto. */}
                        <Button
                          onClick={() => {
                            if (onPlanSelect) {
                              onPlanSelect(tier.id, true); // true = trial
                            }
                          }}
                          disabled={loading}
                          className="w-full"
                        >
                          Probar 14 días gratis
                        </Button>
                        <p className="text-center text-xs text-neutral-500">
                          Sin tarjeta de crédito
                        </p>
                        <Button
                          onClick={() => {
                            if (onPlanSelect) {
                              onPlanSelect(tier.id, false); // false = pagar ahora
                            }
                          }}
                          disabled={loading}
                          variant="outline"
                          className="w-full">
                          Pagar ahora
                        </Button>
                      </>
                    ) : (
                      /* Planes Pro y Pro+ solo tienen opción de pagar */
                      <Button
                        onClick={() => {
                          if (onPlanSelect) {
                            onPlanSelect(tier.id, false); // false = pagar ahora
                          }
                        }}
                        disabled={loading}
                        variant={tier.highlight ? 'default' : 'outline'}
                        className="w-full">
                        Pagar ahora
                      </Button>
                    )}
                  </>
                ) : (
                  /* Comportamiento normal cuando NO es onboarding */
                  <>
                    <Button
                      onClick={() => handlePlanSelection(tier)}
                      disabled={loading || isCurrentPlan(tier.id)}
                      variant={tier.highlight ? 'default' : 'outline'}
                      className="w-full">
                      {isCurrentPlan(tier.id) ? (
                        "Plan Actual"
                      ) : loading ? (
                        "Procesando..."
                      ) : tier.highlight ? (
                        <>
                          Comenzar ahora
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      ) : (
                        <>
                          Comenzar
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                    
                    {/* Botón de prueba - solo para desarrollo */}
                    {/* {!isCurrentPlan(tier.id) && (
                      <Button
                        onClick={() => handleTestPayment(tier)}
                        disabled={loading}
                        variant="outline"
                        className="w-full text-xs border-orange-300 text-orange-600 hover:bg-orange-50"
                      >
                        🧪 Pago de Prueba (Dev)
                      </Button>
                    )} */}
                  </>
                )}
            </TarjetaDePlan>
          ))}
        </div>
      </div>
    </section>
  )
}

export { PricingSection }